import { useEditor, EditorContent, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Typography from '@tiptap/extension-typography'
import DOMPurify from 'dompurify'
import { FC, useEffect, useCallback, useState, useRef } from 'react'
import { cn } from '../../lib/utils'
import {
  Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3,
  Undo, Redo, Minus, Quote, Code, Pilcrow, CornerDownLeft
} from 'lucide-react'

interface TipTapEditorProps {
  content: string
  onChange: (content: string) => void
  placeholder?: string
  editable?: boolean
  className?: string
}

// Elegant toolbar button with keyboard hint
const ToolbarButton: FC<{
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  title: string
  shortcut?: string
  children: React.ReactNode
}> = ({ onClick, isActive, disabled, title, shortcut, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={shortcut ? `${title} (${shortcut})` : title}
    className={cn(
      "relative h-8 w-8 rounded-md flex items-center justify-center",
      "transition-all duration-150 ease-out",
      "text-muted-foreground hover:text-foreground",
      "hover:bg-clinical-teal/10 active:scale-95",
      "disabled:opacity-30 disabled:pointer-events-none",
      isActive && "bg-clinical-teal/15 text-clinical-teal shadow-inner"
    )}
  >
    {children}
  </button>
)

// Divider for toolbar groups
const ToolbarDivider: FC = () => (
  <div className="w-px h-5 bg-border/60 mx-1" />
)

// Fixed toolbar with glass morphism
const EditorToolbar: FC<{ editor: Editor | null }> = ({ editor }) => {
  if (!editor) return null

  return (
    <div className={cn(
      "flex items-center gap-0.5 px-3 py-2",
      "bg-surface-elevated/80 backdrop-blur-md",
      "border-b border-border/40",
      "shadow-sm"
    )}>
      {/* Text formatting */}
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
          shortcut="Ctrl+B"
        >
          <Bold className="h-4 w-4" strokeWidth={2.5} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
          shortcut="Ctrl+I"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          title="Inline Code"
          shortcut="Ctrl+E"
        >
          <Code className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <ToolbarDivider />

      {/* Block formatting */}
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
          shortcut="Ctrl+Alt+1"
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
          shortcut="Ctrl+Alt+2"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
          shortcut="Ctrl+Alt+3"
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          isActive={editor.isActive('paragraph')}
          title="Paragraph"
        >
          <Pilcrow className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <ToolbarDivider />

      {/* Lists */}
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
          shortcut="Ctrl+Shift+8"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
          shortcut="Ctrl+Shift+7"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
          shortcut="Ctrl+Shift+B"
        >
          <Quote className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <ToolbarDivider />

      {/* Insert */}
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Divider"
        >
          <Minus className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHardBreak().run()}
          title="Line Break"
          shortcut="Shift+Enter"
        >
          <CornerDownLeft className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <div className="flex-1" />

      {/* History */}
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
          shortcut="Ctrl+Z"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
          shortcut="Ctrl+Shift+Z"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>
    </div>
  )
}


/**
 * Clinical Manuscript Editor
 * A refined WYSIWYG editor with markdown support, designed for clinical documentation
 */
export const TipTapEditor: FC<TipTapEditorProps> = ({
  content,
  onChange,
  placeholder = 'Begin documenting...',
  editable = true,
  className
}) => {
  const [isFocused, setIsFocused] = useState(false)
  // Fix #263: Track internal edits to prevent cursor-resetting setContent calls.
  // When onUpdate fires from user typing, we set this ref so the content-sync
  // useEffect knows to skip the redundant setContent that resets the cursor.
  const isInternalUpdateRef = useRef(false)

  // Markdown to HTML conversion
  const markdownToHtml = useCallback((md: string): string => {
    if (!md) return '<p></p>'

    let html = md
      // Headers (process in reverse order to avoid ## matching before ###)
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      // Bold and italic combinations
      .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Horizontal rules
      .replace(/^---$/gm, '<hr>')
      // Blockquotes
      .replace(/^> (.*$)/gm, '<blockquote><p>$1</p></blockquote>')

    // Process lines for paragraphs and lists
    const lines = html.split('\n')
    let result = ''
    let inList = false
    let listType = ''

    for (const line of lines) {
      // Skip if already processed as block element
      if (line.match(/^<(h[123]|hr|blockquote)/)) {
        if (inList) {
          result += listType === 'ul' ? '</ul>' : '</ol>'
          inList = false
        }
        result += line
        continue
      }

      // Unordered list items
      if (line.match(/^[\-\*] /)) {
        if (!inList || listType !== 'ul') {
          if (inList) result += listType === 'ul' ? '</ul>' : '</ol>'
          result += '<ul>'
          inList = true
          listType = 'ul'
        }
        result += `<li><p>${line.replace(/^[\-\*] /, '')}</p></li>`
        continue
      }

      // Ordered list items
      if (line.match(/^\d+\. /)) {
        if (!inList || listType !== 'ol') {
          if (inList) result += listType === 'ul' ? '</ul>' : '</ol>'
          result += '<ol>'
          inList = true
          listType = 'ol'
        }
        result += `<li><p>${line.replace(/^\d+\. /, '')}</p></li>`
        continue
      }

      // Close list if we hit non-list content
      if (inList) {
        result += listType === 'ul' ? '</ul>' : '</ol>'
        inList = false
      }

      // Regular paragraph
      if (line.trim()) {
        result += `<p>${line}</p>`
      }
    }

    if (inList) {
      result += listType === 'ul' ? '</ul>' : '</ol>'
    }

    // Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(result || '<p></p>', {
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'strong', 'em', 'code', 'hr', 'blockquote', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: []
    })
  }, [])

  // HTML to Markdown conversion
  const htmlToMarkdown = useCallback((html: string): string => {
    const temp = document.createElement('div')
    temp.innerHTML = html

    const processNode = (node: Node, listContext?: { type: string; index: number }): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || ''
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return ''

      const el = node as Element
      const children = Array.from(el.childNodes)
        .map(child => processNode(child, listContext))
        .join('')

      switch (el.tagName.toLowerCase()) {
        case 'h1': return `# ${children}\n\n`
        case 'h2': return `## ${children}\n\n`
        case 'h3': return `### ${children}\n\n`
        case 'p':
          if (el.parentElement?.tagName.toLowerCase() === 'li') {
            return children
          }
          return `${children}\n\n`
        case 'strong':
        case 'b': return `**${children}**`
        case 'em':
        case 'i': return `*${children}*`
        case 'code': return `\`${children}\``
        case 'blockquote': return `> ${children.trim().replace(/\n\n$/, '')}\n\n`
        case 'ul':
          return Array.from(el.children)
            .map(li => processNode(li, { type: 'ul', index: 0 }))
            .join('')
        case 'ol':
          let idx = 1
          return Array.from(el.children)
            .map(li => processNode(li, { type: 'ol', index: idx++ }))
            .join('')
        case 'li':
          const prefix = listContext?.type === 'ol' ? `${listContext.index}. ` : '- '
          return `${prefix}${children.trim()}\n`
        case 'hr': return '---\n\n'
        case 'br': return '\n'
        default: return children
      }
    }

    return processNode(temp).trim().replace(/\n{3,}/g, '\n\n')
  }, [])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      Typography,
    ],
    content: markdownToHtml(content),
    editable,
    onUpdate: ({ editor }) => {
      isInternalUpdateRef.current = true
      const html = editor.getHTML()
      const markdown = htmlToMarkdown(html)
      onChange(markdown)
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    editorProps: {
      attributes: {
        class: 'clinical-editor-content',
      },
    },
  })

  // Sync content from props (external updates only)
  useEffect(() => {
    if (!editor) return
    // Fix #263: Skip if this content change originated from user typing (onUpdate).
    // Without this guard, every keystroke triggers setContent which resets the cursor.
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false
      return
    }
    if (content !== htmlToMarkdown(editor.getHTML())) {
      editor.commands.setContent(markdownToHtml(content))
    }
  }, [content, editor, markdownToHtml, htmlToMarkdown])

  // Sync editable state
  useEffect(() => {
    if (editor) editor.setEditable(editable)
  }, [editor, editable])

  return (
    <div className={cn(
      "clinical-editor flex flex-col overflow-hidden rounded-xl",
      "bg-card border border-border/50",
      "transition-shadow duration-300",
      isFocused && editable && "ring-2 ring-clinical-teal/20 border-clinical-teal/40",
      className
    )}>
      {editable && <EditorToolbar editor={editor} />}

      {/* Editor content area with manuscript styling */}
      <div className={cn(
        "flex-1 overflow-y-auto",
        "bg-gradient-to-b from-surface-elevated to-surface",
        // Subtle paper texture effect
        "relative before:absolute before:inset-0 before:pointer-events-none",
        "before:bg-[radial-gradient(circle_at_50%_0%,_hsl(var(--clinical-teal)/0.02)_0%,_transparent_50%)]"
      )}>
        <EditorContent
          editor={editor}
          className={cn(
            "h-full",
            "[&_.ProseMirror]:min-h-full [&_.ProseMirror]:p-6 [&_.ProseMirror]:outline-none",
            // Typography
            "[&_.ProseMirror]:font-sans [&_.ProseMirror]:text-[15px] [&_.ProseMirror]:leading-relaxed",
            "[&_.ProseMirror]:text-foreground",
            // Headings - using display font
            "[&_.ProseMirror_h1]:font-display [&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-semibold",
            "[&_.ProseMirror_h1]:text-foreground [&_.ProseMirror_h1]:mt-6 [&_.ProseMirror_h1]:mb-3",
            "[&_.ProseMirror_h1]:tracking-tight [&_.ProseMirror_h1]:border-b [&_.ProseMirror_h1]:border-border/30",
            "[&_.ProseMirror_h1]:pb-2",
            "[&_.ProseMirror_h2]:font-display [&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-semibold",
            "[&_.ProseMirror_h2]:text-foreground [&_.ProseMirror_h2]:mt-5 [&_.ProseMirror_h2]:mb-2",
            "[&_.ProseMirror_h3]:font-display [&_.ProseMirror_h3]:text-lg [&_.ProseMirror_h3]:font-medium",
            "[&_.ProseMirror_h3]:text-foreground [&_.ProseMirror_h3]:mt-4 [&_.ProseMirror_h3]:mb-2",
            // Paragraphs
            "[&_.ProseMirror_p]:my-3",
            // Lists with clinical teal markers
            "[&_.ProseMirror_ul]:my-3 [&_.ProseMirror_ul]:pl-5",
            "[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul_li]:my-1",
            "[&_.ProseMirror_ul_li::marker]:text-clinical-teal",
            "[&_.ProseMirror_ol]:my-3 [&_.ProseMirror_ol]:pl-5",
            "[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol_li]:my-1",
            "[&_.ProseMirror_ol_li::marker]:text-clinical-teal [&_.ProseMirror_ol_li::marker]:font-medium",
            // Blockquotes
            "[&_.ProseMirror_blockquote]:my-4 [&_.ProseMirror_blockquote]:pl-4",
            "[&_.ProseMirror_blockquote]:border-l-2 [&_.ProseMirror_blockquote]:border-clinical-teal/40",
            "[&_.ProseMirror_blockquote]:text-muted-foreground [&_.ProseMirror_blockquote]:italic",
            // Code
            "[&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm",
            "[&_.ProseMirror_code]:bg-muted/50 [&_.ProseMirror_code]:px-1.5 [&_.ProseMirror_code]:py-0.5",
            "[&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:text-clinical-teal",
            // Horizontal rule
            "[&_.ProseMirror_hr]:my-6 [&_.ProseMirror_hr]:border-border/50",
            // Bold and italic
            "[&_.ProseMirror_strong]:font-semibold",
            "[&_.ProseMirror_em]:italic",
            // Placeholder styling
            "[&_.ProseMirror_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
            "[&_.ProseMirror_.is-editor-empty:first-child::before]:text-muted-foreground/50",
            "[&_.ProseMirror_.is-editor-empty:first-child::before]:float-left",
            "[&_.ProseMirror_.is-editor-empty:first-child::before]:h-0",
            "[&_.ProseMirror_.is-editor-empty:first-child::before]:pointer-events-none",
            "[&_.ProseMirror_.is-editor-empty:first-child::before]:italic"
          )}
        />
      </div>

      {/* Subtle bottom gradient fade */}
      <div className="h-4 bg-gradient-to-t from-surface to-transparent pointer-events-none" />
    </div>
  )
}


